/**
 * GoPay MoR §13 — shipping/export disclosure (a) vs (b).
 *
 * (a) Active DeliveryPolicy + empty allowedCountryCodes → unrestricted honesty
 *     (never pickup-only wording).
 * (b) No active DeliveryPolicy → pickup-only / no international parcel.
 *
 * When allowlist is non-empty, disclose listed ISO codes (post-migrate honesty).
 */

export type ShippingDisclosureKind =
  | 'unrestricted'
  | 'pickup_only'
  | 'allowlist';

export interface ShippingDisclosureInput {
  /** True when shippingLightEnabled AND an active DeliveryPolicy is returned. */
  readonly hasActiveDeliveryPolicy: boolean;
  /**
   * DeliveryPolicy.allowedCountryCodes from eligible API.
   * Empty / null / undefined + active policy → unrestricted (never pickup-only).
   */
  readonly allowedCountryCodes?: readonly string[] | null;
}

export interface ShippingDisclosureResult {
  readonly kind: ShippingDisclosureKind;
  /** Uppercased ISO-2 codes when kind === allowlist. */
  readonly countryCodes: readonly string[];
}

export function resolveShippingDisclosure(
  input: ShippingDisclosureInput,
): ShippingDisclosureResult {
  if (!input.hasActiveDeliveryPolicy) {
    return { kind: 'pickup_only', countryCodes: [] };
  }
  const raw = input.allowedCountryCodes;
  const codes =
    raw == null
      ? []
      : raw
          .map((c) => c.trim().toUpperCase())
          .filter((c) => c.length === 2);
  const unique = [...new Set(codes)].sort();
  if (unique.length === 0) {
    return { kind: 'unrestricted', countryCodes: [] };
  }
  return { kind: 'allowlist', countryCodes: unique };
}
